import type { ISchedule, IUserFit } from "@gettactic/api";
import { isAfter, isBefore } from "date-fns";

/**
 * Parse a schedule timestamp the same way the rest of the app does — strip
 * the trailing `Z` and read it in the viewer's local zone (see
 * SchedulesGroups / resourceHoverStatus). Parsing it as real UTC shifts
 * reservations by the timezone offset, misclassifying current ones as
 * upcoming (which hid the occupancy info on search rows).
 */
export const parseScheduleDate = (value: string) =>
  new Date(value.replace("Z", ""));

export function getScheduleTimeline(schedules: ISchedule[] | undefined) {
  const now = new Date();

  if (!schedules || schedules.length === 0) {
    return {
      now,
      currentSchedule: null as ISchedule | null,
      upcomingSchedule: null as ISchedule | null,
      pastSchedule: null as ISchedule | null
    };
  }

  const currentSchedule =
    schedules.find((schedule) => {
      const start = parseScheduleDate(schedule.start);
      const end = parseScheduleDate(schedule.end);
      return isAfter(now, start) && isBefore(now, end);
    }) || null;

  const upcomingSchedule =
    schedules
      .filter((schedule) => {
        const start = parseScheduleDate(schedule.start);
        return isAfter(start, now);
      })
      .sort(
        (a, b) =>
          parseScheduleDate(a.start).getTime() -
          parseScheduleDate(b.start).getTime()
      )[0] || null;

  const pastSchedule =
    schedules
      .filter((schedule) => {
        const end = parseScheduleDate(schedule.end);
        return isBefore(end, now);
      })
      .sort(
        (a, b) =>
          parseScheduleDate(b.end).getTime() -
          parseScheduleDate(a.end).getTime()
      )[0] || null;

  return {
    now,
    currentSchedule,
    upcomingSchedule,
    pastSchedule
  };
}

export function getUsersFromSchedule(schedule: ISchedule): IUserFit[] {
  const users: IUserFit[] = [];

  if (schedule.slots && schedule.slots.length > 0) {
    for (const slot of schedule.slots) {
      if (slot.user?.id && slot.user) {
        users.push({
          id: slot.user.id,
          profile_photo_url: slot.user.profile_photo_url,
          name: slot.user.name,
          roles: slot.user.roles
        });
      }
    }
    return users;
  }

  if (schedule.user?.id) {
    users.push({
      id: schedule.user.id,
      profile_photo_url: schedule.user.profile_photo_url,
      name: schedule.user.name,
      roles: schedule.user.roles
    });
  } else if (schedule.organizer?.id) {
    users.push({
      id: schedule.organizer.id,
      profile_photo_url: schedule.organizer.profile_photo_url,
      name: schedule.organizer.name,
      roles: schedule.organizer.roles
    });
  } else if (schedule.creator?.id) {
    users.push({
      id: schedule.creator.id,
      profile_photo_url: schedule.creator.profile_photo_url,
      name: schedule.creator.name,
      roles: schedule.creator.roles
    });
  }

  return users;
}
